@if (isWorthShowing) {
    <!-- Full bar height, with the trigger centred inside it, so top-full below
         means the bottom of the BAR. Hung off the trigger instead, the panel
         opened 9px up inside the header: this trigger is bare text, half the
         height of the bordered chips on the other side, and mt-2 from its
         bottom never reached the bar's edge. -->
    <div class="relative h-full flex items-center">
        <button
            type="button"
            (click)="toggle()"
            data-testid="store-switcher-trigger"
            [attr.aria-expanded]="isOpen"
            class="flex items-center gap-1.5 px-1.5 py-1 rounded-lg hover:bg-[var(--surface-hover)] transition-colors cursor-pointer"
        >
            <!-- businessName, like every other place the panel names the
                 store: the header beside this, the settings sidebar, the
                 design screen. Signup briefly wrote the OWNER's name here,
                 which is what once sent this to tenant.name; it now writes
                 the store name, so this is back on the shared field. -->
            <span
                class="hidden sm:block text-sm font-semibold text-[var(--text-color)] truncate max-w-[120px] xl:max-w-[160px]"
            >
                {{ loggedInUser?.tenant?.businessName }}
            </span>
            <i
                class="pi pi-angle-down text-xs text-[var(--text-color-secondary)] transition-transform duration-300"
                [ngClass]="{ 'rotate-180': isOpen }"
            ></i>
        </button>

        <!-- Drawn by hand rather than with an overlay component, because every
             other menu in this header is - one pattern, one set of styles. -->
        <!-- mt-1, not the mt-2 the other menus write, because it is measured
             from a different line: they drop 8px below a 46px chip that ends
             short of the bar, this drops from the bar's own edge. Both land on
             the same row - matching the number here would sit 5px lower. -->
        @if (isOpen) {
            <div
                data-testid="store-switcher-menu"
                class="absolute left-0 top-full mt-0.5 w-72 rounded-xl overflow-hidden bg-[var(--surface-card)] border border-[var(--surface-border)] shadow-xl z-50"
            >
                <p
                    class="px-4 pt-3 pb-1.5 text-xs font-semibold tracking-wider uppercase text-[var(--text-color-secondary)]"
                >
                    Your stores
                </p>

                @if (isLoading) {
                    <div class="px-4 pb-3 space-y-2">
                        @for (item of [1, 2]; track $index) {
                            <p-skeleton width="100%" height="40px" class="!rounded-lg" />
                        }
                    </div>
                } @else {
                    <div class="max-h-72 overflow-y-auto">
                        @for (tenant of tenantMemberships; track tenant.membershipId) {
                            <button
                                type="button"
                                (click)="onSelect(tenant)"
                                [attr.data-testid]="'store-switcher-shop-' + tenant.membershipId"
                                [attr.aria-current]="isCurrent(tenant) ? 'true' : null"
                                [disabled]="switchingMembershipId !== null"
                                class="flex items-center gap-3 w-full px-4 py-2.5 text-left transition-colors duration-150 enabled:cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                                [ngClass]="
                                    isCurrent(tenant)
                                        ? 'bg-[var(--surface-hover)]'
                                        : 'hover:bg-[var(--surface-hover)]'
                                "
                            >
                                <span
                                    class="w-8 h-8 rounded-lg shrink-0 overflow-hidden flex items-center justify-center bg-[var(--surface-ground)]"
                                >
                                    @if (tenant.logoUrl) {
                                        <img
                                            [src]="tenant.logoUrl"
                                            [alt]="tenant.businessName"
                                            class="w-full h-full object-cover"
                                        />
                                    } @else {
                                        <i
                                            class="pi pi-shop text-xs text-[var(--text-color-secondary)]"
                                        ></i>
                                    }
                                </span>

                                <span class="flex-1 min-w-0">
                                    <span
                                        class="block text-sm truncate text-[var(--text-color)]"
                                        [ngClass]="{ 'font-semibold': isCurrent(tenant) }"
                                        >{{ tenant.businessName }}</span
                                    >
                                </span>

                                @if (switchingMembershipId === tenant.membershipId) {
                                    <i class="pi pi-spin pi-spinner text-xs"></i>
                                } @else if (isCurrent(tenant)) {
                                    <i class="pi pi-check text-xs text-[var(--primary-color)]"></i>
                                }
                            </button>
                        }
                    </div>

                    @if (canOpenAnother) {
                        <div class="border-t border-[var(--surface-border)] mt-1 pt-2 pb-3">
                            <!-- Live at the ceiling too. A disabled control that a
                             merchant cannot act on, in front of a screen that
                             explains why and lists what they hold, is the worst
                             of both: the explanation exists and nobody reaches
                             it. Clicking goes there instead of nowhere. -->
                            <button
                                type="button"
                                (click)="onOpenAnother()"
                                data-testid="store-switcher-open-another"
                                class="flex items-center gap-3 w-full px-4 py-2 text-sm transition-colors duration-150 cursor-pointer text-[var(--primary-color)] hover:bg-[var(--surface-hover)]"
                            >
                                <i class="pi pi-plus text-xs"></i>
                                <span>Open another store</span>
                            </button>

                            @if (atCeiling) {
                                <!-- A disabled control that does not say why is just
                                 a dead end. This is the only place the merchant
                                 is told, because the wizard they would have gone
                                 to is exactly what is being refused. -->
                                <div
                                    data-testid="store-switcher-limit-note"
                                    class="flex items-start gap-2 mx-4 mt-2 px-3 py-2 rounded-lg bg-[var(--surface-ground)] border border-[var(--surface-border)]"
                                >
                                    <i
                                        class="pi pi-info-circle text-xs mt-0.5 shrink-0 text-[var(--text-color-secondary)]"
                                    ></i>
                                    <p
                                        class="text-xs leading-relaxed text-[var(--text-color-secondary)]"
                                    >
                                        You are using all {{ limit }} stores. Tap above to see them.
                                    </p>
                                </div>
                            } @else {
                                <!-- Indented to the button's LABEL, not its icon, so
                                 the two read as one block. -->
                                <p
                                    class="pl-10 pr-4 mt-1.5 text-xs leading-relaxed text-[var(--text-color-secondary)]"
                                    data-testid="store-switcher-usage"
                                >
                                    {{ ownedCount }} of {{ limit }} used
                                </p>
                            }
                        </div>
                    }
                }
            </div>
        }
    </div>
}
